import { useEffect, useRef } from "react";
import { ZONE_ASPECT, clickToUnit, unitYToTopPct } from "../hooks/useStrikeZone";

// Strike-zone overlay: click the body to record a pitch (exact legacy math),
// plus mouse drag-to-move (grip) and corner resize. Replaces ClickZone.
// When `locked`, the grip and resize handle are hidden so they can't be
// mis-grabbed mid-recording — only the clickable body remains.
export const StrikeZoneOverlay = ({
  box,
  x,
  y,
  setX,
  setY,
  onSubmit,
  setPosition,
  setSize,
  locked = false,
}) => {
  const drag = useRef(null);
  useEffect(() => {
    if (locked) drag.current = null;
  }, [locked]);

  useEffect(() => {
    const cancel = () => { drag.current = null; };
    window.addEventListener("blur", cancel);
    document.addEventListener("visibilitychange", cancel);
    return () => {
      window.removeEventListener("blur", cancel);
      document.removeEventListener("visibilitychange", cancel);
    };
  }, []);

  const handleClick = (e) => {
    // Only a direct click on the box body is a pitch — the grip, resize handle
    // and indicator are children and must never register as a location.
    if (e.target !== e.currentTarget) return;
    // Use the box's viewport rect (not offsetLeft/Top) so the click maths is
    // correct regardless of the box's offset parent — the overlay now lives
    // inside the responsive .video-box, not at page origin.
    const rect = e.currentTarget.getBoundingClientRect();
    // Absolute-positioned marker percentages use the padding box. Match that
    // origin and keep fractional CSS pixels instead of rounded clientWidth.
    const style = getComputedStyle(e.currentTarget);
    const borderLeft = parseFloat(style.borderLeftWidth) || 0;
    const borderTop = parseFloat(style.borderTopWidth) || 0;
    const width = rect.width - borderLeft - (parseFloat(style.borderRightWidth) || 0);
    if (width <= 0) return;
    const { x: ux, y: uy } = clickToUnit(
      e.clientX,
      e.clientY,
      rect.left + borderLeft,
      rect.top + borderTop,
      width
    );
    setX(ux);
    setY(uy);
    onSubmit(ux, uy);
  };

  // Drag deltas are screen px; convert to fractions of the .video-box so the
  // box geometry (fractions) scales with the responsive video.
  const videoBox = (el) => el.closest(".video-box");
  const startMove = (e) => {
    if (locked || e.button !== 0 || drag.current) return;
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    const vb = videoBox(e.currentTarget);
    drag.current = {
      mode: "move",
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      ox: box.x,
      oy: box.y,
      vbW: vb ? vb.clientWidth : 1,
      vbH: vb ? vb.clientHeight : 1,
    };
  };
  const startResize = (e) => {
    if (locked || e.button !== 0 || drag.current) return;
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    const vb = videoBox(e.currentTarget);
    drag.current = { mode: "resize", pointerId: e.pointerId, startX: e.clientX, oSize: box.size, vbW: vb ? vb.clientWidth : 1 };
  };
  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    if (locked || e.buttons === 0) {
      drag.current = null;
      return;
    }
    if (d.mode === "move") {
      setPosition(d.ox + (e.clientX - d.startX) / d.vbW, d.oy + (e.clientY - d.startY) / d.vbH);
    } else {
      setSize(d.oSize + (e.clientX - d.startX) / d.vbW);
    }
  };
  const endDrag = (e) => {
    if (drag.current?.pointerId === e.pointerId) drag.current = null;
  };

  const gripStyle = {
    position: "absolute",
    top: -24,
    left: 0,
    padding: "2px 8px",
    fontSize: 12,
    color: "#fff",
    background: "#2563eb",
    borderRadius: 4,
    cursor: "move",
    userSelect: "none",
    touchAction: "none",
  };
  const resizeStyle = {
    position: "absolute",
    right: -7,
    bottom: -7,
    width: 14,
    height: 14,
    background: "#2563eb",
    border: "2px solid #fff",
    borderRadius: 3,
    cursor: "nwse-resize",
    touchAction: "none",
  };
  // Locked-state hint. pointerEvents:none so it never intercepts a pitch click.
  const lockStyle = {
    position: "absolute",
    top: -24,
    left: 0,
    padding: "2px 6px",
    fontSize: 12,
    lineHeight: "16px",
    color: "#fff",
    background: "#6b7280",
    borderRadius: 4,
    userSelect: "none",
    pointerEvents: "none",
  };

  return (
    <div
      className="zone-body"
      data-testid="zone-body"
      style={{
        left: `${box.x * 100}%`,
        top: `${box.y * 100}%`,
        width: `${box.size * 100}%`,
        aspectRatio: 1 / ZONE_ASPECT,
      }}
      onClick={handleClick}
    >
      {/* pointer-events:none so a click on the last-pitch marker passes through
          to the body and records a pitch at that spot with the CORRECT coords
          (legacy clicked the dot itself and submitted wrong coords). Positioned
          by unit fraction so it tracks the box at any size. */}
      <div
        className="zone-point"
        data-testid="zone-point"
        hidden={x === null || y === null}
        style={{
          left: `${(x / 90) * 100}%`,
          top: `${unitYToTopPct(y)}%`,
          transform: "translate(-50%, -50%)",
          pointerEvents: "none",
        }}
      />
      {!locked && (
        <>
          <div
            className="zone-move-grip"
            data-testid="zone-move-grip"
            style={gripStyle}
            onPointerDown={startMove}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onLostPointerCapture={endDrag}
            title="拖曳移動好球帶框"
            aria-label="拖曳移動好球帶框"
          >
            ✛ 移動
          </div>
          <div
            className="zone-resize-handle"
            data-testid="zone-resize-handle"
            style={resizeStyle}
            onPointerDown={startResize}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onLostPointerCapture={endDrag}
            title="拖曳縮放（鎖定比例）"
            aria-label="拖曳縮放好球帶框（鎖定比例）"
          />
        </>
      )}
      {locked && (
        <div className="zone-lock" data-testid="zone-lock" style={lockStyle} title="好球帶已鎖定（L 解鎖）" aria-label="好球帶已鎖定">
          🔒
        </div>
      )}
    </div>
  );
};
